// ===========================================
// Extra Lesson Requests Management Page
// ===========================================
// Admin interface for managing extra lesson requests from parents

import { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  Skeleton,
  Tooltip,
  Stack,
  TablePagination,
  InputAdornment,
} from '@mui/material';
import {
  Check as ApproveIcon,
  Close as RejectIcon,
  Schedule as ScheduleIcon,
  Done as CompleteIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Add as AddIcon,
  EditCalendar as RescheduleIcon,
  EventBusy as CancelIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { formatUTCDate } from '../../utils/dateUtils';
import {
  useExtraLessonRequests,
  useExtraLessonPendingCount,
  useApproveExtraLessonRequest,
  useRejectExtraLessonRequest,
  useScheduleExtraLessonRequest,
  useRescheduleExtraLessonRequest,
  useCompleteExtraLessonRequest,
  useAdminCancelExtraLesson,
  ExtraLessonFilters,
} from '../../hooks/useExtraLessonRequests';
import { ExtraLessonRequest, ExtraLessonRequestStatus } from '../../api/booking.api';
import BookExtraLessonDialog, {
  TeacherAvailabilityScheduler,
  SchedulingState,
  emptyScheduling,
} from '../../components/admin/BookExtraLessonDialog';

// ===========================================
// STATUS CONFIGURATION
// ===========================================

const statusConfig: Record<ExtraLessonRequestStatus, { label: string; bgcolor: string; color: string }> = {
  PENDING: { label: 'Pending', bgcolor: '#9DA5AF', color: 'white' },
  APPROVED: { label: 'Approved', bgcolor: '#4580E4', color: 'white' },
  SCHEDULED: { label: 'Scheduled', bgcolor: '#96DAC9', color: '#080808' },
  COMPLETED: { label: 'Completed', bgcolor: '#FFCE00', color: '#080808' },
  REJECTED: { label: 'Rejected', bgcolor: '#ff4040', color: 'white' },
  CANCELLED: { label: 'Cancelled', bgcolor: '#9DA5AF', color: 'white' },
};

// ===========================================
// STAT CARDS COMPONENT
// ===========================================

function ExtraLessonStats() {
  const { data: countData, isLoading } = useExtraLessonPendingCount();
  const pendingCount = countData?.count ?? 0;

  if (isLoading) return <Skeleton variant="rectangular" height={120} />;

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid item xs={12} sm={6} md={3}>
        <Card sx={{ bgcolor: '#FCF6E6' }}>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Pending Requests
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#FFCE00' }}>
              {pendingCount}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Awaiting approval
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

// ===========================================
// REQUEST DETAIL DIALOG
// ===========================================

type DetailMode = 'view' | 'reschedule' | 'cancel';

interface RequestDetailDialogProps {
  open: boolean;
  request: ExtraLessonRequest | null;
  onClose: () => void;
  /** Open straight into a given mode (used by the row quick-action buttons). */
  initialMode?: DetailMode;
}

function RequestDetailDialog({ open, request, onClose, initialMode = 'view' }: RequestDetailDialogProps) {
  const [mode, setMode] = useState<DetailMode>(initialMode);
  const [scheduling, setScheduling] = useState<SchedulingState>(emptyScheduling);
  const [cancelReason, setCancelReason] = useState('');

  const rescheduleMutation = useRescheduleExtraLessonRequest();
  const cancelMutation = useAdminCancelExtraLesson();

  // Reset to the requested initial mode whenever the dialog opens or the request changes.
  // For reschedule we prefill the scheduler from the current booking.
  useEffect(() => {
    setScheduling(
      initialMode === 'reschedule' && request
        ? {
            teacherId: request.teacher?.id ?? '',
            selectedDate: request.scheduledDate ? new Date(request.scheduledDate) : null,
            selectedTime: request.scheduledTime ?? '',
            duration: request.duration ?? 30,
            roomId: request.roomId ?? '',
          }
        : emptyScheduling
    );
    setMode(initialMode);
    setCancelReason('');
  }, [request?.id, open, initialMode]);

  if (!request) return null;

  // Cancel/reschedule only make sense for time-reserving statuses.
  const canModify = request.status === 'SCHEDULED' || request.status === 'APPROVED';

  const handleClose = () => {
    setMode('view');
    setScheduling(emptyScheduling);
    setCancelReason('');
    onClose();
  };

  const startReschedule = () => {
    // Prefill the scheduler with the current booking so the admin only changes
    // what they need. Teacher is fixed for a reschedule (same teacher).
    setScheduling({
      teacherId: request.teacher?.id ?? '',
      selectedDate: request.scheduledDate ? new Date(request.scheduledDate) : null,
      selectedTime: request.scheduledTime ?? '',
      duration: request.duration ?? 30,
      roomId: request.roomId ?? '',
    });
    setMode('reschedule');
  };

  const canSubmitReschedule =
    !!scheduling.selectedDate && !!scheduling.selectedTime;

  const handleConfirmReschedule = () => {
    if (!canSubmitReschedule) return;
    rescheduleMutation.mutate(
      {
        requestId: request.id,
        input: {
          scheduledDate: format(scheduling.selectedDate!, 'yyyy-MM-dd'),
          scheduledTime: scheduling.selectedTime,
          duration: scheduling.duration,
        },
      },
      { onSuccess: handleClose }
    );
  };

  const handleConfirmCancel = () => {
    cancelMutation.mutate(
      { requestId: request.id, reason: cancelReason || undefined },
      { onSuccess: handleClose }
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {mode === 'reschedule'
          ? 'Reschedule Extra Lesson'
          : mode === 'cancel'
          ? 'Cancel Extra Lesson'
          : 'Request Details'}
      </DialogTitle>
      <DialogContent>
        {mode === 'view' && (
          <Box sx={{ pt: 2, display: 'grid', gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Student</Typography>
              <Typography>{request.student.firstName} {request.student.lastName}</Typography>
            </Box>
            {request.instrument && (
              <Box>
                <Typography variant="caption" color="text.secondary">Instrument</Typography>
                <Typography>{request.instrument.name}</Typography>
              </Box>
            )}
            {request.lessonType && (
              <Box>
                <Typography variant="caption" color="text.secondary">Lesson Type</Typography>
                <Typography>{request.lessonType.name}</Typography>
              </Box>
            )}
            <Box>
              <Typography variant="caption" color="text.secondary">Requested</Typography>
              <Typography>{format(new Date(request.createdAt), 'dd MMM yyyy HH:mm')}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Reason</Typography>
              <Typography>{request.reason}</Typography>
            </Box>
            {request.notes && (
              <Box>
                <Typography variant="caption" color="text.secondary">Notes</Typography>
                <Typography>{request.notes}</Typography>
              </Box>
            )}
            {request.adminNotes && (
              <Box>
                <Typography variant="caption" color="text.secondary">Admin Notes</Typography>
                <Typography>{request.adminNotes}</Typography>
              </Box>
            )}
            {request.makeupForLesson && (
              <Box>
                <Typography variant="caption" color="text.secondary">Making up for</Typography>
                <Typography>
                  {request.makeupForLesson.name} ({request.makeupForLesson.term.name})
                </Typography>
              </Box>
            )}
            {request.staffNote && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Staff note (admin + teacher)
                </Typography>
                <Typography sx={{ whiteSpace: 'pre-wrap' }}>{request.staffNote}</Typography>
                {request.shareNoteWithFamily && (
                  <Chip label="Shared with family" size="small" sx={{ mt: 0.5 }} />
                )}
              </Box>
            )}
            {request.teacher && (
              <Box>
                <Typography variant="caption" color="text.secondary">Teacher</Typography>
                <Typography>{request.teacher.user.firstName} {request.teacher.user.lastName}</Typography>
              </Box>
            )}
            {request.scheduledDate && (
              <Box>
                <Typography variant="caption" color="text.secondary">Scheduled Date & Time</Typography>
                <Typography>
                  {formatUTCDate(request.scheduledDate, { day: '2-digit', month: 'short', year: 'numeric' })} at {request.scheduledTime}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {mode === 'reschedule' && (
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Pick a new date and time with {request.teacher?.user.firstName}{' '}
              {request.teacher?.user.lastName}. The family will be notified by email.
            </Alert>
            <TeacherAvailabilityScheduler
              value={scheduling}
              onChange={setScheduling}
              disabled={rescheduleMutation.isPending}
              lockTeacher
            />
          </Box>
        )}

        {mode === 'cancel' && (
          <Box sx={{ pt: 2 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Cancel this extra lesson? The slot will be released and any make-up
              credit used will be refunded.
            </Alert>
            <TextField
              label="Cancellation reason (optional)"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              fullWidth
              multiline
              rows={2}
              inputProps={{ maxLength: 500 }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {mode === 'view' && (
          <>
            <Button onClick={handleClose}>Close</Button>
            {canModify && (
              <Button
                variant="outlined"
                startIcon={<ScheduleIcon />}
                onClick={startReschedule}
              >
                Reschedule
              </Button>
            )}
            {canModify && (
              <Button
                color="error"
                startIcon={<RejectIcon />}
                onClick={() => setMode('cancel')}
              >
                Cancel Lesson
              </Button>
            )}
          </>
        )}

        {mode === 'reschedule' && (
          <>
            <Button onClick={() => setMode('view')} disabled={rescheduleMutation.isPending}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmReschedule}
              disabled={!canSubmitReschedule || rescheduleMutation.isPending}
            >
              {rescheduleMutation.isPending ? 'Rescheduling...' : 'Confirm Reschedule'}
            </Button>
          </>
        )}

        {mode === 'cancel' && (
          <>
            <Button onClick={() => setMode('view')} disabled={cancelMutation.isPending}>
              Back
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmCancel}
              disabled={cancelMutation.isPending}
            >
              {cancelMutation.isPending ? 'Cancelling...' : 'Confirm Cancel'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ===========================================
// APPROVE DIALOG
// ===========================================

interface ApproveDialogProps {
  open: boolean;
  request: ExtraLessonRequest | null;
  onConfirm: (notes?: string) => void;
  onClose: () => void;
  loading: boolean;
}

function ApproveDialog({ open, request, onConfirm, onClose, loading }: ApproveDialogProps) {
  const [notes, setNotes] = useState('');

  if (!request) return null;

  const handleConfirm = () => {
    onConfirm(notes);
    setNotes('');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Approve Request?</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            {request.student.firstName} {request.student.lastName} will be notified that their request has been approved.
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Internal Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add notes about this approval..."
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={loading}>
          {loading ? 'Approving...' : 'Approve'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ===========================================
// REJECT DIALOG
// ===========================================

interface RejectDialogProps {
  open: boolean;
  request: ExtraLessonRequest | null;
  onConfirm: (reason: string) => void;
  onClose: () => void;
  loading: boolean;
}

function RejectDialog({ open, request, onConfirm, onClose, loading }: RejectDialogProps) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  if (!request) return null;

  const handleConfirm = () => {
    if (!reason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }
    onConfirm(reason);
    setReason('');
    setError('');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reject Request?</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {request.student.firstName} {request.student.lastName} will be notified with the reason for rejection.
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason for Rejection"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setError('');
            }}
            error={!!error}
            helperText={error || 'Explain why this request cannot be approved'}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={loading || !reason.trim()}
        >
          {loading ? 'Rejecting...' : 'Reject'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


// ===========================================
// SCHEDULE DIALOG
// ===========================================

interface ScheduleDialogProps {
  open: boolean;
  request: ExtraLessonRequest | null;
  onConfirm: (data: {
    scheduledDate: string;
    scheduledTime: string;
    duration: number;
    teacherId: string;
    roomId?: string;
  }) => void;
  onClose: () => void;
  loading: boolean;
}


function ScheduleDialog({ open, request, onConfirm, onClose, loading }: ScheduleDialogProps) {
  const [scheduling, setScheduling] = useState<SchedulingState>(emptyScheduling);

  if (!request) return null;

  const canSubmit = !!scheduling.teacherId && !!scheduling.selectedDate && !!scheduling.selectedTime;

  const handleConfirm = () => {
    if (!canSubmit) return;
    onConfirm({
      scheduledDate: format(scheduling.selectedDate!, 'yyyy-MM-dd'),
      scheduledTime: scheduling.selectedTime,
      duration: scheduling.duration,
      teacherId: scheduling.teacherId,
      roomId: scheduling.roomId || undefined,
    });
    setScheduling(emptyScheduling);
  };

  const handleClose = () => {
    setScheduling(emptyScheduling);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Schedule Extra Lesson — {request.student.firstName} {request.student.lastName}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <TeacherAvailabilityScheduler value={scheduling} onChange={setScheduling} disabled={loading} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={loading || !canSubmit}>
          {loading ? 'Scheduling...' : 'Schedule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ===========================================
// MAIN PAGE COMPONENT
// ===========================================

export default function ExtraLessonRequestsPage() {
  const [statusFilter, setStatusFilter] = useState<ExtraLessonRequestStatus | undefined>();
  const [selectedRequest, setSelectedRequest] = useState<ExtraLessonRequest | null>(null);
  const [dialogType, setDialogType] = useState<
    'detail' | 'approve' | 'reject' | 'schedule' | 'reschedule' | 'cancel' | null
  >(null);
  const [bookDialogOpen, setBookDialogOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Fetch data
  const filters: ExtraLessonFilters = statusFilter ? { status: statusFilter } : {};
  const { data: requests = [], isLoading, refetch } = useExtraLessonRequests(filters);

  // Mutations
  const approveMutation = useApproveExtraLessonRequest();
  const rejectMutation = useRejectExtraLessonRequest();
  const scheduleMutation = useScheduleExtraLessonRequest();
  const completeMutation = useCompleteExtraLessonRequest();

  // Filter and paginate requests
  const filteredRequests = useMemo(() => {
    if (!search) return requests;
    const searchLower = search.toLowerCase();
    return requests.filter((req) => {
      const studentName = `${req.student.firstName} ${req.student.lastName}`.toLowerCase();
      const instrumentName = req.instrument?.name.toLowerCase() || '';
      const teacherName = req.teacher
        ? `${req.teacher.user.firstName} ${req.teacher.user.lastName}`.toLowerCase()
        : '';
      return (
        studentName.includes(searchLower) ||
        instrumentName.includes(searchLower) ||
        teacherName.includes(searchLower)
      );
    });
  }, [requests, search]);

  const paginatedRequests = useMemo(() => {
    return filteredRequests.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredRequests, page, rowsPerPage]);

  // Handlers
  const handleStatusChange = (_event: React.SyntheticEvent, value: any) => {
    setStatusFilter(value === 'ALL' ? undefined : value);
  };

  const openDialog = (request: ExtraLessonRequest, type: typeof dialogType) => {
    setSelectedRequest(request);
    setDialogType(type);
  };

  const closeDialog = () => {
    setSelectedRequest(null);
    setDialogType(null);
  };

  const handleApprove = (notes?: string) => {
    if (selectedRequest) {
      approveMutation.mutate({ requestId: selectedRequest.id, adminNotes: notes });
      closeDialog();
    }
  };

  const handleReject = (reason: string) => {
    if (selectedRequest) {
      rejectMutation.mutate({ requestId: selectedRequest.id, adminNotes: reason });
      closeDialog();
    }
  };

  const handleSchedule = (data: Parameters<ScheduleDialogProps['onConfirm']>[0]) => {
    if (selectedRequest) {
      scheduleMutation.mutate({ requestId: selectedRequest.id, ...data });
      closeDialog();
    }
  };

  const handleComplete = (request: ExtraLessonRequest) => {
    completeMutation.mutate(request.id);
  };

  // Render
  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Extra Lesson Requests
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setBookDialogOpen(true)}
          >
            Book Extra Class
          </Button>
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetch()} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Stats */}
      <ExtraLessonStats />

      {/* Tabs for status filtering */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <Tabs
          value={statusFilter || 'ALL'}
          onChange={handleStatusChange}
          sx={{ px: 2 }}
        >
          <Tab label="All" value="ALL" />
          <Tab label="Pending" value="PENDING" />
          <Tab label="Approved" value="APPROVED" />
          <Tab label="Scheduled" value="SCHEDULED" />
          <Tab label="Completed" value="COMPLETED" />
          <Tab label="Rejected" value="REJECTED" />
        </Tabs>
      </Card>

      {/* Search */}
      <TextField
        placeholder="Search by student, instrument, or teacher..."
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(0); // Reset to first page on search
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
        fullWidth
      />

      {/* Data Table */}
      {isLoading ? (
        <Skeleton variant="rectangular" height={400} />
      ) : filteredRequests.length === 0 ? (
        <Alert severity="info">
          {search ? 'No requests match your search.' : 'No extra lesson requests found.'}
        </Alert>
      ) : (
        <>
          <TableContainer component={Card}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#FCF6E6' }}>
                  <TableCell>Student</TableCell>
                  <TableCell>Instrument</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Requested</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRequests.map((request) => (
                <TableRow key={request.id} hover>
                  <TableCell>{request.student.firstName} {request.student.lastName}</TableCell>
                  <TableCell>{request.instrument?.name || '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {request.reason}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Chip
                        label={statusConfig[request.status].label}
                        size="small"
                        sx={{
                          bgcolor: statusConfig[request.status].bgcolor,
                          color: statusConfig[request.status].color,
                        }}
                      />
                      {/* makeupForLessonId survives a cancel (which refunds and
                          clears makeupCreditId), so the chip matches the detail. */}
                      {(request.makeupForLessonId || request.makeupCreditId) && (
                        <Chip label="Make-up" size="small" variant="outlined" />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>{format(new Date(request.createdAt), 'dd MMM')}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => openDialog(request, 'detail')}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      {(request.status === 'PENDING' || request.status === 'APPROVED') && (
                        <Tooltip title="Schedule">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => openDialog(request, 'schedule')}
                          >
                            <ScheduleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      {request.status === 'PENDING' && (
                        <>
                          <Tooltip title="Approve (without scheduling)">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => openDialog(request, 'approve')}
                            >
                              <ApproveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => openDialog(request, 'reject')}
                            >
                              <RejectIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}

                      {request.status === 'SCHEDULED' && (
                        <Tooltip title="Mark Complete">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleComplete(request)}
                          >
                            <CompleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      {(request.status === 'SCHEDULED' || request.status === 'APPROVED') && (
                        <>
                          <Tooltip title="Reschedule">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => openDialog(request, 'reschedule')}
                            >
                              <RescheduleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancel Lesson">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => openDialog(request, 'cancel')}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredRequests.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </TableContainer>
        </>
      )}

      {/* Book Extra Class Dialog */}
      <BookExtraLessonDialog
        open={bookDialogOpen}
        onClose={() => setBookDialogOpen(false)}
      />

      {/* Dialogs */}
      <RequestDetailDialog
        open={dialogType === 'detail' || dialogType === 'reschedule' || dialogType === 'cancel'}
        request={selectedRequest}
        onClose={closeDialog}
        initialMode={
          dialogType === 'reschedule'
            ? 'reschedule'
            : dialogType === 'cancel'
            ? 'cancel'
            : 'view'
        }
      />
      <ApproveDialog
        open={dialogType === 'approve'}
        request={selectedRequest}
        onConfirm={handleApprove}
        onClose={closeDialog}
        loading={approveMutation.isPending}
      />
      <RejectDialog
        open={dialogType === 'reject'}
        request={selectedRequest}
        onConfirm={handleReject}
        onClose={closeDialog}
        loading={rejectMutation.isPending}
      />
      <ScheduleDialog
        open={dialogType === 'schedule'}
        request={selectedRequest}
        onConfirm={handleSchedule}
        onClose={closeDialog}
        loading={scheduleMutation.isPending}
      />
    </Box>
  );
}
